import { Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Polls `/version.json` on a fixed interval to detect when a fresher
 * SPA bundle has been deployed to S3 + CloudFront. When the served
 * hash diverges from the one captured at boot, emits
 * `updateAvailable$ = true` so the UpdateBanner can prompt the
 * operator to refresh.
 *
 * Mirrors `src/web/src/app/core/services/app-update.service.ts` —
 * kept in lockstep so the staging/prod UX is identical across the
 * admin shell and the POS workspace. The deploy pipeline regenerates
 * `public/version.json` on every build (`scripts/generate-version.js`
 * runs in `prebuild`); workflows upload it with `Cache-Control:
 * no-cache` so this poll always sees the live value.
 *
 * Gated on `environment.production` so polling never runs against
 * dev / staging — `ng serve` + the staging hot-rebuild loop already
 * handle freshness there.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly POLL_INTERVAL = 2 * 60 * 1000; // 2 minutes
  private readonly DISMISS_BACKOFF = 5 * 60 * 1000; // 5 minutes after dismiss
  private readonly VERSION_URL = 'version.json';
  private timerId: ReturnType<typeof setInterval> | null = null;
  private currentHash: string | null = null;
  private readonly platformId = inject(PLATFORM_ID);

  /** Emits true when a newer version is detected on the server. */
  readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

  /** Call once after app bootstrap (e.g. from AppComponent or APP_INITIALIZER). */
  start(): void {
    if (!isPlatformBrowser(this.platformId)) return; // skip during SSR prerender
    if (!environment.production) return;          // skip in dev / staging
    if (this.timerId) return;                     // already running

    this.checkVersion();                          // initial fetch to capture current hash
    this.startPolling(this.POLL_INTERVAL);

    // also check when tab regains focus (user returns from another app)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /** User dismissed the banner — hide it but keep polling at a slower rate. */
  dismiss(): void {
    this.updateAvailable$.next(false);
    this.startPolling(this.DISMISS_BACKOFF);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private stop(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private startPolling(interval: number): void {
    if (this.timerId) clearInterval(this.timerId);
    this.timerId = setInterval(() => this.checkVersion(), interval);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkVersion();
    }
  };

  private async checkVersion(): Promise<void> {
    try {
      const res = await fetch(`${this.VERSION_URL}?_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;

      const data: { hash: string } = await res.json();
      if (!data?.hash) return;

      if (this.currentHash === null) {
        // first fetch — store as baseline
        this.currentHash = data.hash;
        return;
      }

      if (data.hash !== this.currentHash) {
        this.updateAvailable$.next(true);
      }
    } catch {
      // network error — ignore, will retry next interval
    }
  }
}
